
import log from "plugins/loglevel/index"

import * as retry from "retry"
import Promise from "plugins/bluebird/index"
import WindowState from "app/window_state/index"
import DataRetriever from "app/data_retriever"
import {
  captureMessage,
} from "app/error_reporting"


/* eslint-disable no-unused-vars */
enum NetworkLatencyStatuses {
  LATENCY_UNKNOWN  = "LATENCY_UNKNOWN",
  LATENCY_DETECTED = "LATENCY_DETECTED",
}
/* eslint-enable no-unused-vars */

interface NetworkLatencyToSpaciousResult {
  latency_value_in_ms: number,
  status: NetworkLatencyStatuses,
}

// Represents unknown
const UNKNOWN_NETWORK_LATENCY_TO_SERVER_MS_VALUE = -1
let network_latency_to_server_ms = UNKNOWN_NETWORK_LATENCY_TO_SERVER_MS_VALUE
let status = NetworkLatencyStatuses.LATENCY_UNKNOWN


const ENDPOINT_IDENTIFIER = "network_latency_to_spacious"


const refreshNetworkLatencyPromise = (): Promise<void> => {
  // Set fallback value, although `beforeSend` should set it anyway
  let start_time = new Date().getTime()

  const timeout_options = {
    minTimeout: 10 * 1000,
    maxTimeout: 60 * 1000,
    factor:     2,
    // Should become 10,20,40,60
    retries:    4,
  }

  // We are not waiting locally, only having different timeout values on AJAX
  const retry_operation = retry.operation({
    retries:    5,
    factor:     1,
    minTimeout: 0,
    maxTimeout: 0,
    randomize:  false,
  })

  const url = "/ping"
  const fetch_options: RequestInit = {
    method:         "GET",
    // Shouldn't allow redirect by default due to potential bug
    // Allow later if really needed
    redirect:       "error" as "error",
    // Safer default, update to other value is required
    referrerPolicy: "same-origin" as "same-origin",
    // Safer default, update to other value is required
    credentials:    "same-origin" as "same-origin",
    // Just explicitly stating the default
    cache:          "default" as "default",
    headers:        {
      // AJAX simulation
      "X-Requested-With": "XMLHttpRequest",
    },
  }

  return new Promise((resolve) => {
    // currentAttempt is 1-based, tested manually
    retry_operation.attempt((currentAttempt) => {
      // `attempt` is zero-indexed in `createTimeout` but `currentAttempt` is 1 based
      const current_timeout_ms = retry.createTimeout(currentAttempt - 1, timeout_options)

      // region timeout
      const abort_controller = new AbortController()
      fetch_options.signal = abort_controller.signal

      // https://stackoverflow.com/a/50101022/838346
      const timeoutId = setTimeout(() => {
        abort_controller.abort()
      }, current_timeout_ms)
      // endregion timeout

      start_time = new Date().getTime()
      window.fetch(
        url,
        fetch_options,
      )
      .then((response) => {
        clearTimeout(timeoutId)
        if (response.ok) {
          const new_network_latency_to_server_ms = new Date().getTime() - start_time
          status = NetworkLatencyStatuses.LATENCY_DETECTED
          // If unknown, assign detected value anyway
          //
          // If new value < existing value, assign new value too
          // since values fetched during script load / dom load
          // might be inaccurate due to busy main thread
          if (network_latency_to_server_ms === UNKNOWN_NETWORK_LATENCY_TO_SERVER_MS_VALUE ||
            new_network_latency_to_server_ms < network_latency_to_server_ms) {

            network_latency_to_server_ms = new_network_latency_to_server_ms
          }

          log.info(`Network Latency to Spacious: ${network_latency_to_server_ms}ms`)

          resolve()
          return
        }

        const context = {
          fetch_options:        JSON.stringify(fetch_options),
          http_status_code:     response.status,
          endpoint_identifier:  ENDPOINT_IDENTIFIER,
          current_attempt:      currentAttempt,
        }

        // No error should be reported for AJAX errors
        // caused by page being unloaded
        if (WindowState.isUnloaded()) {
          resolve()
          return
        }

        // Could be gateway server not ready
        if (response.status === 404) {
          resolve()
          return
        }

        if (retry_operation.retry(new Error(response.statusText))) {
          return
        }

        // Status Code 0 Could be caused by:
        // - Illegal CORS (We don't use it anyway)
        // - Firewall blocking/filtering (We can't fix)
        // - Request is cancelled (not `abort()`) (Expected)
        // - Browser extension (any Adblocker) (We can't fix)
        //
        // https://stackoverflow.com/a/14507670/838346
        //
        // So we don't report status code 0
        //
        // Some clients got strange response codes
        if (response.status !== 0 && isSaneUserAgent() && isSaneLocation()) {
          captureMessage(
            `Error occurred when using ajax with status <${(response.status || "unknown")}> on endpoint <${ENDPOINT_IDENTIFIER}>`,
            context,
          )
        }

        // Resolve with status stayed in unknown
        resolve()
      })
      .catch((error) => {
        if (retry_operation.retry(error)) {
          return
        }

        // Resolve with status stayed in unknown
        resolve()
      })
    })
  })
}

function getNetworkLatencyToSpacious(): NetworkLatencyToSpaciousResult {
  return {
    latency_value_in_ms:  network_latency_to_server_ms,
    status:               status,
  }
}

function getNetworkLatencyToSpaciousPromise(): Promise<NetworkLatencyToSpaciousResult> {
  return refreshNetworkLatencyPromise()
  .then(() => {
    return {
      latency_value_in_ms:  network_latency_to_server_ms,
      status:               status,
    }
  })
}

// Not reporting errors from "insane" user agents
function isSaneUserAgent(): boolean {
  return [
    /Baiduspider-render/i,
    /YandexBot/i,
  ].every((regex) => !regex.test(window.navigator.userAgent))
}

// Not reporting errors from "insane" locations
function isSaneLocation(): boolean {
  return !DataRetriever.get("current-location-country-code-is-china")
}


export {
  getNetworkLatencyToSpacious,
  getNetworkLatencyToSpaciousPromise,
  refreshNetworkLatencyPromise as refreshNetworkLatencyToSpaciousPromise,
  NetworkLatencyStatuses as NetworkLatencyToSpaciousStatues,
}
